import React from "react";

import type { UserID } from "../../../../../hooks/useAccount";
import {
  type ProjectAccessRequest,
  type ProjectAccessRequestID,
  type ProjectUserAssignableRole,
  ProjectUserAssignableRoles,
  ProjectUserRoleDisplayName,
  useAcceptProjectAccessRequest,
} from "../../../../../hooks/useProjectSharing";
import { ShareRow, type ShareRowRef } from "./ShareRow";

interface RequestAccessRowProps {
  request: ProjectAccessRequest;
  onRemove?: (userId: ProjectAccessRequestID) => void;
  containerIntersectionObserver?: IntersectionObserver | null;
  onAccepted?: (userId: UserID, role: string) => void;
  isProRoleEligible?: (role: ProjectUserAssignableRole) => boolean;
  isDisabled?: boolean;
}

export const RequestAccessRowProject = React.forwardRef<ShareRowRef, RequestAccessRowProps>(
  (
    { request, onRemove, containerIntersectionObserver, onAccepted, isProRoleEligible, isDisabled },
    ref
  ) => {
    const {
      mutateAsync: mutateAcceptProjectAccessRequest,
      isPending: isAcceptPending,
      isError: isAcceptError,
    } = useAcceptProjectAccessRequest({});

    return (
      <ShareRow
        ref={ref}
        roles={ProjectUserAssignableRoles}
        proRoles={new Set<(typeof ProjectUserAssignableRoles)[number]>(["editor"])}
        isProRoleEligible={isProRoleEligible}
        roleDisplayName={ProjectUserRoleDisplayName}
        entity={request}
        containerIntersectionObserver={containerIntersectionObserver}
        onUpdate={async (_, newRole) => {
          await mutateAcceptProjectAccessRequest({
            projectAccessRequestId: request.accessRequestId,
            role: newRole,
          });
          onAccepted?.(request.userId, newRole);
        }}
        onRemove={() => {
          onRemove?.(request.accessRequestId);
        }}
        isPending={isAcceptPending || isDisabled}
        isError={isAcceptError}
        isRequestAccess
        removeActionText={"Deny Access"}
      />
    );
  }
);
